import {
  useCheckVerificationMutation,
  useVerificationMutation,
} from '@entities/Authentication/rtk-query.ts';
import type {
  CheckVerification,
  Verification,
  VerificationRespond,
} from '@entities/Authentication/types.ts';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSupportPageRedirect } from '@pages/support/model.ts';
import { doTransformRTKErrorResponse } from '@shared/helpers/index.ts';
import { useAppDispatch } from '@shared/hooks';
import { openNotifyModal } from '@src/library/redux/modal';

export const useAuthDialogs = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { redirectToSupportPage } = useSupportPageRedirect();
  const onCantVerifyCode = useCallback(
    ({ onConfirm }: { onConfirm: () => void }) => {
      dispatch(
        openNotifyModal({
          needClose: true,
          isOnConfirmShouldNotWorkOnCloseModal: true,
          text: 'Если у вас не получилось зарегистрироваться, обратитесь в техническую поддержку',
          confirmText: 'Техническая поддержка',
          title: 'Уведомление',
          confirm: () => {
            redirectToSupportPage();
            onConfirm();
          },
        })
      );
    },
    [navigate, dispatch]
  );
  return {
    onCantVerifyCode,
  };
};

type HelpersType = {
  onError?: (errMessage: string, errorField?: string) => void;
  onSuccess?: (data?: VerificationRespond) => void;
};
type VerifyCallbackType = Verification & HelpersType;
type CheckVerifyCallbackType = CheckVerification & HelpersType;
const onPromiseFulfilled = ({
  onError,
  onSuccess,
  response,
}: HelpersType & { response: unknown }) => {
  const resp = response as { error?: FetchBaseQueryError; data?: VerificationRespond };
  if (resp?.error) {
    const transformedResponseError = doTransformRTKErrorResponse(resp.error);
    const errorMessage = transformedResponseError?.firstError;
    const errorField = transformedResponseError?.firstErrorFiledName;
    if (errorMessage) {
      onError && onError(errorMessage, errorField);
    }
  } else {
    onSuccess && onSuccess(resp?.data);
  }
};

export const useVerify = () => {
  const [verify, { isLoading: isVerificationLoading }] = useVerificationMutation();

  const [checkVerify, { isLoading: isCheckVerificationLoading }] = useCheckVerificationMutation();

  const onVerificationButtonClick = useCallback(
    async ({ onSuccess, onError, ...props }: VerifyCallbackType) => {
      const response = await verify(props);
      onPromiseFulfilled({ response, onSuccess, onError });
      return response;
    },
    [verify]
  );

  const onCheckVerificationButtonClick = useCallback(
    async ({ onSuccess, onError, ...props }: CheckVerifyCallbackType) => {
      const response = await checkVerify(props);
      onPromiseFulfilled({ response, onSuccess, onError });
    },
    [checkVerify]
  );

  const onPhoneVerification = useCallback(
    async (props: Omit<VerifyCallbackType, 'email'>) => {
      await onVerificationButtonClick(props);
    },
    [onVerificationButtonClick]
  );

  const onEmailVerification = useCallback(
    async (props: Omit<VerifyCallbackType, 'phonenumber' | 'type'>) => {
      await onVerificationButtonClick({ ...props });
    },
    [onVerificationButtonClick]
  );

  const onCheckPhoneVerification = useCallback(
    async (props: Omit<CheckVerifyCallbackType, 'email'>) => {
      await onCheckVerificationButtonClick(props);
    },
    [onCheckVerificationButtonClick]
  );

  const onCheckEmailVerification = useCallback(
    async (props: Omit<CheckVerifyCallbackType, 'phonenumber'>) => {
      await onCheckVerificationButtonClick({ ...props });
    },
    [onCheckVerificationButtonClick]
  );

  return {
    onVerificationButtonClick,
    onEmailVerification,
    onPhoneVerification,
    onCheckPhoneVerification,
    onCheckEmailVerification,
    isVerificationLoading,
    isCheckVerificationLoading,
    onCheckVerificationButtonClick,
  };
};
